import type { AssessmentTemplate } from '../../model/assessments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { toAssessmentTemplate } from './template-response'

interface AssessmentTemplateDuplicateApiOptions {
  templateId: string | number
  onStart?: () => void
  onSuccess?: (template: AssessmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `POST /admin-api/assessment-templates/{template_id}/duplicate` (contract §6). No body; the
 * `201` carries the new row, so `template.id` is where to navigate. Normalised as `show` (B7).
 */
export const useAssessmentTemplateDuplicateApi = async (
  options: AssessmentTemplateDuplicateApiOptions,
): Promise<AssessmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assessment-templates.duplicate', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template = toAssessmentTemplate(data.data)

    if (!template) {
      const msg = 'Unexpected error. The assessment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    toast.success('Assessment template duplicated')
    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
